import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { CompactLogoIcon, ECompactLogoIconType } from '../../../web/components/svgs/icons/CompactLogoIcon';
import { AdminDashboardPageLayout } from '../../containers/AdminDashboardPageLayout';
import { supportSharedStyles as ss } from './styles/supportSharedStyles';
import { SUPPORT_FAVICON_HREF, SUPPORT_MASTHEAD_HEIGHT } from './supportLayoutConstants';

interface AdminSupportPageLayoutProps {
	children: React.ReactNode;
	sidebarChildren?: React.ReactNode;
	contentStyles?: StyleDeclarationValue[];
	/** Enables the support-only phone shell; standard admin pages retain their 768px minimum. */
	narrowLayout?: boolean;
	/**
	 * Tickets in the view on screen, shown in the browser tab as "(4) Support". The point is a
	 * backgrounded tab: an agent working elsewhere sees the count change without switching to it.
	 * Omitted (or zero) leaves the plain title.
	 */
	ticketCount?: number;
}

const SUPPORT_PAGE_TITLE = 'Support';

/** Past this the exact number stops being actionable and starts truncating the tab's own label. */
const MAX_TITLE_TICKET_COUNT = 99;

export function formatSupportDocumentTitle(ticketCount?: number): string {
	if (!ticketCount || ticketCount <= 0) return SUPPORT_PAGE_TITLE;
	const shown = ticketCount > MAX_TITLE_TICKET_COUNT ? `${MAX_TITLE_TICKET_COUNT}+` : String(ticketCount);
	return `(${shown}) ${SUPPORT_PAGE_TITLE}`;
}

function useSupportDocumentMetadata(ticketCount?: number) {
	React.useEffect(() => {
		const originalFavicons = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]')).map(link => ({
			link,
			href: link.getAttribute('href'),
		}));

		for (const { link } of originalFavicons) {
			link.setAttribute('href', SUPPORT_FAVICON_HREF);
		}

		return () => {
			for (const { link, href } of originalFavicons) {
				if (link.getAttribute('href') !== SUPPORT_FAVICON_HREF) continue;
				if (href === null) link.removeAttribute('href');
				else link.setAttribute('href', href);
			}
		};
	}, []);

	const title = formatSupportDocumentTitle(ticketCount);
	React.useEffect(() => {
		const originalTitle = document.title;
		document.title = title;

		return () => {
			// Leave a title something else has since claimed; only ours is ours to put back.
			if (document.title === title) {
				document.title = originalTitle;
			}
		};
	}, [title]);
}

function SupportNavHeader() {
	return (
		<Link aria-label='Lev Admin home' className={css(ss.supportNavHeader)} title='Lev Admin home' to='/accounts'>
			<CompactLogoIcon height={30} type={ECompactLogoIconType.White} width={30} />
		</Link>
	);
}

/** Shared full-bleed admin shell for the support inbox, reports, and settings surfaces. */
export function AdminSupportPageLayout({
	children,
	sidebarChildren,
	contentStyles,
	narrowLayout = true,
	ticketCount,
}: AdminSupportPageLayoutProps) {
	useSupportDocumentMetadata(ticketCount);

	return (
		<AdminDashboardPageLayout
			compactNav
			fullBleed
			mastheadHeight={SUPPORT_MASTHEAD_HEIGHT}
			mastheadNavigationFillsSpace
			navHeader={<SupportNavHeader />}
			navStyles={[ss.supportNav]}
			showMastheadDivider={false}
			narrowLayout={narrowLayout}
			sidebarChildren={sidebarChildren}
			contentStyles={contentStyles}
		>
			{children}
		</AdminDashboardPageLayout>
	);
}
